import { useEffect, useRef, useState, type ReactNode } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  FilterStrip,
  ResultFooter,
  UnavailableNotice,
  WindowLine,
} from "../components/filters/ConsoleFilterContract";
import { SurfaceSkeleton } from "../components/SurfaceSkeleton";
import { LiveTimestamp } from "../components/LiveTimestamp";
import {
  CONSOLE_WINDOW_INCIDENTS,
  incidentsFilterChips,
  incidentsResultCopy,
  type ConsoleFilterChipLabel,
} from "../data/console-filter-contract";
import { humanizeLabel } from "../data/alert-queue";
import {
  canLockIncident,
  canStartIncidentReview,
  fetchIncidentAttachments,
  fetchIncidentList,
  INCIDENT_SEVERITY_VALUES,
  INCIDENT_STATUS_VALUES,
  lockIncident,
  requestIncidentEvidenceMediaReadUrl,
  reviewStateLabel,
  startIncidentReview,
  summarizeIncidentEvidenceReadiness,
  type IncidentAttachment,
  type IncidentListRead,
  type IncidentRow,
  type IncidentSeverityValue,
  type IncidentStatusValue,
} from "../data/incidents";
import { useReadModel } from "../data/useReadModel";
import { cadSlideInProps } from "../lib/cad-list-motion";
import { deliverRemoteFile } from "../lib/file-delivery";

// Incident feed (CONSOLE-INCIDENTS-READ-SURFACE) over the hosted management views.
// Lane 8 S2: status/site/severity filters run on the host before the 200-row window.
// Shared FilterStrip / WindowLine / ResultFooter / UnavailableNotice own the chrome.
// Evidence METADATA loads lazily per incident. Open/download media is gated on
// storageAttached and goes through field-evidence-signer only.

function severityTone(severity: string | null): string {
  if (severity === "high" || severity === "critical") return "incident__chip--alarm";
  if (severity === "medium") return "incident__chip--warn";
  return "incident__chip--muted";
}

function reviewTone(label: string): string {
  if (label === "Locked") return "incident__chip--alarm";
  if (label === "Under review") return "incident__chip--warn";
  return "incident__chip--muted";
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count.toLocaleString()} ${count === 1 ? singular : plural}`;
}

function evidenceFileName(attachment: IncidentAttachment, signedContentType: string | null): string {
  const displayName = attachment.displayName?.trim();
  if (displayName) return displayName;
  const contentType = signedContentType ?? attachment.contentType;
  const extension =
    contentType === "image/jpeg"
      ? "jpg"
      : contentType === "image/png"
        ? "png"
        : contentType === "application/pdf"
          ? "pdf"
          : "bin";
  return `secops-incident-evidence.${extension}`;
}

function isStatusValue(value: string): value is IncidentStatusValue {
  return (INCIDENT_STATUS_VALUES as readonly string[]).includes(value);
}

function isSeverityValue(value: string): value is IncidentSeverityValue {
  return (INCIDENT_SEVERITY_VALUES as readonly string[]).includes(value);
}

type IncidentLoad =
  | { readonly kind: "read"; readonly value: IncidentListRead }
  | { readonly kind: "error"; readonly message: string };

interface KeyedIncidentLoad {
  readonly requestKey: string;
  readonly outcome: IncidentLoad;
}

export function IncidentFeed({
  client,
  accountId,
  timeZone,
  canManage = false,
  onOpenIncident,
  activeIncidentId = null,
  emptyAction,
  onScopeChange,
  scopeToken = "",
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  /** evidence.manage - review/lock actions only exist when true (fail closed). */
  canManage?: boolean;
  /** Opens one incident into the detail desk. Omitted keeps the feed exactly as it was. */
  onOpenIncident?: (incident: IncidentRow) => void;
  /** The incident open in the master-detail record pane; its row reads as selected. */
  activeIncidentId?: string | null;
  /**
   * The one blue next action of the empty state (board Implement rule 9). The page owns it
   * because navigation (react-router Link) has no business inside this surface.
   */
  emptyAction?: ReactNode;
  /** Clears master-detail selection when the list scope changes (filters/account/support). */
  onScopeChange?: () => void;
  /** Opaque generation token from the page (account + support). Changes invalidate scope. */
  scopeToken?: string;
}) {
  const [siteFilter, setSiteFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [siteOptions, setSiteOptions] = useState<string[]>([]);
  const [openEvidence, setOpenEvidence] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<
    Record<string, IncidentAttachment[] | "loading" | "error">
  >({});
  const [mediaRequests, setMediaRequests] = useState<Record<string, "opening" | "error">>({});
  const [reviewBusy, setReviewBusy] = useState<Record<string, "review" | "lock">>({});
  const [reviewFailures, setReviewFailures] = useState<Record<string, string>>({});
  const [lockConfirm, setLockConfirm] = useState<IncidentRow | null>(null);

  const requestKey = JSON.stringify({
    accountId,
    scopeToken,
    siteFilter,
    statusFilter,
    severityFilter,
  });
  const currentRequestKeyRef = useRef(requestKey);
  currentRequestKeyRef.current = requestKey;
  const previousRequestKeyRef = useRef(requestKey);

  useEffect(() => {
    if (previousRequestKeyRef.current === requestKey) return;
    previousRequestKeyRef.current = requestKey;
    onScopeChange?.();
    setOpenEvidence(null);
    setLockConfirm(null);
    setReviewFailures({});
  }, [requestKey, onScopeChange]);

  useEffect(() => {
    setSiteFilter("");
    setStatusFilter("");
    setSeverityFilter("");
    setSiteOptions([]);
  }, [client, accountId, scopeToken]);

  const { status, data, reload } = useReadModel<KeyedIncidentLoad>(
    async () => {
      try {
        const filters: {
          siteNames?: string[];
          statuses?: IncidentStatusValue[];
          severities?: IncidentSeverityValue[];
        } = {};
        if (siteFilter.length > 0) filters.siteNames = [siteFilter];
        if (isStatusValue(statusFilter)) filters.statuses = [statusFilter];
        if (isSeverityValue(severityFilter)) filters.severities = [severityFilter];
        const value = await fetchIncidentList(client, accountId, filters);
        return { requestKey, outcome: { kind: "read", value } };
      } catch {
        return {
          requestKey,
          outcome: { kind: "error", message: "Could not load incidents." },
        };
      }
    },
    [client, requestKey],
  );
  const currentLoad = data?.requestKey === requestKey ? data : null;
  const currentPending = status === "loading" || currentLoad === null;
  const currentError =
    currentLoad?.outcome.kind === "error" ? currentLoad.outcome.message : null;
  const incidentRead =
    currentLoad?.outcome.kind === "read" ? currentLoad.outcome.value : null;

  useEffect(() => {
    if (incidentRead?.status !== "ok") return;
    setSiteOptions((current) => {
      const next = new Set(current);
      for (const row of incidentRead.rows) {
        if (row.siteName !== null && row.siteName.trim().length > 0) {
          next.add(row.siteName.trim());
        }
      }
      return [...next].sort((left, right) => left.localeCompare(right));
    });
  }, [incidentRead]);

  // Seed site options from an unfiltered bounded read so chips stay available after a filter.
  useEffect(() => {
    let cancelled = false;
    void fetchIncidentList(client, accountId, {})
      .then((read) => {
        if (cancelled || read.status !== "ok") return;
        setSiteOptions((current) => {
          const next = new Set(current);
          for (const row of read.rows) {
            if (row.siteName !== null && row.siteName.trim().length > 0) {
              next.add(row.siteName.trim());
            }
          }
          return [...next].sort((left, right) => left.localeCompare(right));
        });
      })
      .catch(() => {
        // Table owns visible errors; picker options keep last safe values.
      });
    return () => {
      cancelled = true;
    };
  }, [client, accountId, scopeToken]);

  if (currentPending && incidentRead === null && currentError === null) {
    return <SurfaceSkeleton rows={4} label="Loading incidents" />;
  }
  if (currentError !== null) {
    return (
      <div className="report__state-pane">
        <div>
          <p className="surface__state surface__state--error" role="alert">
            {currentError}
          </p>
          <button className="report__retry" type="button" onClick={reload}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const rows = incidentRead?.status === "ok" ? incidentRead.rows : [];
  const capped = incidentRead?.status === "ok" ? incidentRead.capped : false;
  const summary = summarizeIncidentEvidenceReadiness(rows);
  const windowCopy = incidentsResultCopy({
    shownCount: rows.length,
    siteFilter,
    statusFilter,
    severityFilter,
  });
  const filterChips = incidentsFilterChips({ siteFilter, statusFilter, severityFilter });
  const lanes: Array<{ label: string; value: string; tone: "ok" | "muted" }> = [
    { label: "Incident read model", value: "Available", tone: "ok" },
    { label: "Evidence metadata", value: "Available", tone: "ok" },
    { label: "Review and lock state", value: "Available", tone: "ok" },
    { label: "Media upload/download", value: "Existing files only", tone: "ok" },
    { label: "DAR/PDF/CSV exports", value: "Not wired", tone: "muted" },
  ];

  function clearAll(): void {
    setSiteFilter("");
    setStatusFilter("");
    setSeverityFilter("");
  }

  function removeFilterChip(label: ConsoleFilterChipLabel): void {
    if (label === "Site") setSiteFilter("");
    if (label === "Status") setStatusFilter("");
    if (label === "Severity") setSeverityFilter("");
  }

  function toggleEvidence(incidentId: string): void {
    if (openEvidence === incidentId) {
      setOpenEvidence(null);
      return;
    }
    setOpenEvidence(incidentId);
    if (attachments[incidentId] === undefined) {
      setAttachments((prev) => ({ ...prev, [incidentId]: "loading" }));
      fetchIncidentAttachments(client, accountId, incidentId)
        .then((loaded) => setAttachments((prev) => ({ ...prev, [incidentId]: loaded })))
        .catch(() => setAttachments((prev) => ({ ...prev, [incidentId]: "error" })));
    }
  }

  function clearKey<T>(map: Record<string, T>, key: string): Record<string, T> {
    const next = { ...map };
    delete next[key];
    return next;
  }

  async function applyReviewAction(incident: IncidentRow, action: "review" | "lock"): Promise<void> {
    if (!canManage) return;
    const incidentId = incident.incidentId;
    if (reviewBusy[incidentId] !== undefined) return;

    setReviewBusy((prev) => ({ ...prev, [incidentId]: action }));
    setReviewFailures((prev) => clearKey(prev, incidentId));

    const outcome =
      action === "lock"
        ? await lockIncident(client, accountId, incidentId)
        : await startIncidentReview(client, accountId, incidentId);

    setReviewBusy((prev) => clearKey(prev, incidentId));
    if (outcome.ok) {
      setLockConfirm(null);
      reload();
    } else {
      setReviewFailures((prev) => ({ ...prev, [incidentId]: outcome.reason }));
    }
  }

  async function openStoredMedia(attachment: IncidentAttachment): Promise<void> {
    if (!attachment.storageAttached) return;
    setMediaRequests((prev) => ({ ...prev, [attachment.attachmentId]: "opening" }));
    try {
      const media = await requestIncidentEvidenceMediaReadUrl(
        client,
        accountId,
        attachment.attachmentId,
      );
      await deliverRemoteFile({
        url: media.signedUrl,
        fileName: evidenceFileName(attachment, media.contentType),
        title: "SecOps incident evidence",
      });
      setMediaRequests((prev) => {
        const next = { ...prev };
        delete next[attachment.attachmentId];
        return next;
      });
    } catch {
      setMediaRequests((prev) => ({ ...prev, [attachment.attachmentId]: "error" }));
    }
  }

  const nothingYet =
    incidentRead?.status === "empty" && incidentRead.reason === "nothing_yet";
  const noMatch = incidentRead?.status === "empty" && incidentRead.reason === "no_match";

  return (
    <div className="incident-feed">
      <dl className="incident__facts" aria-label="Evidence readiness summary">
        <div className="incident__fact">
          <dt>Incidents</dt>
          <dd>{summary.totalIncidents.toLocaleString()}</dd>
        </div>
        <div className="incident__fact">
          <dt>Attachments</dt>
          <dd>{pluralize(summary.totalAttachments, "file")}</dd>
        </div>
        <div className="incident__fact">
          <dt>Evidence present</dt>
          <dd>{pluralize(summary.incidentsWithAttachments, "incident")}</dd>
        </div>
        <div className="incident__fact">
          <dt>Review state</dt>
          <dd>
            {summary.underReviewCount.toLocaleString()} review /{" "}
            {summary.lockedCount.toLocaleString()} locked
          </dd>
        </div>
      </dl>

      <ul className="incident__lanes" aria-label="Task 60 evidence lanes">
        {lanes.map((lane) => (
          <li key={lane.label} className="incident__lane">
            <span className="incident__lane-label">{lane.label}</span>
            <strong className={`incident__lane-chip incident__lane-chip--${lane.tone}`}>
              {lane.value}
            </strong>
          </li>
        ))}
      </ul>

      <div className="report__toolbar">
        <label className="report__filter">
          <span className="report__filter-label">Site</span>
          <select
            aria-label="Filter by site"
            value={siteFilter}
            onChange={(event) => setSiteFilter(event.target.value)}
          >
            <option value="">All sites</option>
            {siteOptions.map((site) => (
              <option key={site} value={site}>
                {site}
              </option>
            ))}
          </select>
        </label>
        <label className="report__filter">
          <span className="report__filter-label">Status</span>
          <select
            aria-label="Filter by status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">All statuses</option>
            {INCIDENT_STATUS_VALUES.filter((value) => value !== "draft").map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="report__filter">
          <span className="report__filter-label">Severity</span>
          <select
            aria-label="Filter by severity"
            value={severityFilter}
            onChange={(event) => setSeverityFilter(event.target.value)}
          >
            <option value="">All severities</option>
            {INCIDENT_SEVERITY_VALUES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
      </div>

      <FilterStrip
        chips={filterChips}
        onClearAll={clearAll}
        onRemoveChip={removeFilterChip}
      />

      <WindowLine variant={CONSOLE_WINDOW_INCIDENTS} />

      {incidentRead?.status === "unavailable" ? (
        <UnavailableNotice
          surface="incidents"
          reason={incidentRead.reason}
          kicker="unavailable_not_empty"
        />
      ) : nothingYet ? (
        <>
          <div className="empty-hero" role="status">
            <h2 className="empty-hero__title">No filed incidents yet.</h2>
            <p className="empty-hero__body">
              Reports from the Guard app appear here with status, severity, and evidence.
              Each record arrives with the filing officer and timestamps, then moves through
              review to a locked state.
            </p>
            {emptyAction !== undefined ? (
              <div className="empty-hero__actions">{emptyAction}</div>
            ) : null}
          </div>
          <div className="ghost-queue" aria-hidden="true">
            <p className="ghost-record__label">Queue · severity · status · age</p>
            {[42, 55, 38, 48].map((width) => (
              <div className="ghost-row" key={width}>
                <span className="ghost-bar" style={{ width: 54 }} />
                <span className="ghost-bar" style={{ width: `${width}%` }} />
                <span className="ghost-pill ghost-pill--end" />
              </div>
            ))}
          </div>
          <ResultFooter state={windowCopy.state} foot={windowCopy.foot} />
        </>
      ) : noMatch ? (
        <div className="report__table-pane">
          <div className="report__empty" role="status">
            <div>
              <p className="report__state-kicker">Empty</p>
              <p>{windowCopy.foot}</p>
              <button type="button" onClick={clearAll}>
                Clear all
              </button>
            </div>
          </div>
          <ResultFooter state={windowCopy.state} foot={windowCopy.foot} />
        </div>
      ) : incidentRead?.status === "ok" ? (
        <div className="report__table-pane">
          <ul className="incident__rows">
            {rows.map((incident, index) => {
              const evidence = attachments[incident.incidentId];
              const reviewLabel = reviewStateLabel(incident);
              const enter = cadSlideInProps(index);
              const active = incident.incidentId === activeIncidentId;
              return (
                <li
                  key={incident.incidentId}
                  className={`incident__row${active ? " incident__row--active" : ""}${enter.className ? ` ${enter.className}` : ""}`}
                  style={enter.style}
                >
                  <div className="incident__head">
                    <span className="incident__title">{incident.title}</span>
                    <span className="schedule__chip incident__chip">
                      {humanizeLabel(incident.status)}
                    </span>
                    <span
                      className={`schedule__chip incident__chip ${reviewTone(reviewLabel)}`}
                    >
                      {reviewLabel}
                    </span>
                    {incident.severity !== null ? (
                      <span
                        className={`schedule__chip incident__chip ${severityTone(incident.severity)}`}
                      >
                        {humanizeLabel(incident.severity)}
                      </span>
                    ) : null}
                    {incident.category !== null ? (
                      <span className="schedule__chip incident__chip incident__chip--muted">
                        {humanizeLabel(incident.category)}
                      </span>
                    ) : null}
                  </div>
                  <p className="incident__context">
                    {[incident.siteName, incident.routeName, incident.checkpointName]
                      .filter(Boolean)
                      .join(" - ") || "no location context"}
                    {" | reported by "}
                    {incident.reportedByLabel ?? "unknown"}
                    {incident.reviewedByLabel !== null
                      ? ` | reviewed by ${incident.reviewedByLabel}`
                      : ""}
                    {" | occurred "}
                    <LiveTimestamp iso={incident.occurredAt} timeZone={timeZone} />
                  </p>
                  <div className="incident__meta">
                    {incident.notesPresent ? (
                      <span className="schedule__chip incident__chip--muted">notes on file</span>
                    ) : null}
                    <button
                      type="button"
                      className="builder__navbtn"
                      onClick={() => toggleEvidence(incident.incidentId)}
                    >
                      Evidence ({incident.attachmentCount}
                      {incident.photoCount > 0
                        ? `, ${incident.photoCount} photo${incident.photoCount === 1 ? "" : "s"}`
                        : ""}
                      )
                    </button>
                    {onOpenIncident !== undefined ? (
                      <button
                        type="button"
                        className="builder__navbtn"
                        onClick={() => onOpenIncident(incident)}
                      >
                        Open
                      </button>
                    ) : null}
                    {canManage && canStartIncidentReview(incident) ? (
                      <button
                        type="button"
                        className="builder__navbtn"
                        disabled={reviewBusy[incident.incidentId] !== undefined}
                        onClick={() => {
                          void applyReviewAction(incident, "review");
                        }}
                      >
                        {reviewBusy[incident.incidentId] === "review"
                          ? "Starting review..."
                          : "Start review"}
                      </button>
                    ) : null}
                    {canManage && canLockIncident(incident) ? (
                      <button
                        type="button"
                        className="builder__navbtn"
                        disabled={reviewBusy[incident.incidentId] !== undefined}
                        onClick={() => {
                          setReviewFailures((prev) => clearKey(prev, incident.incidentId));
                          setLockConfirm(incident);
                        }}
                      >
                        Lock incident
                      </button>
                    ) : null}
                  </div>
                  {reviewFailures[incident.incidentId] !== undefined &&
                  lockConfirm?.incidentId !== incident.incidentId ? (
                    <p className="surface__state surface__state--error" role="alert">
                      {reviewFailures[incident.incidentId]}
                    </p>
                  ) : null}
                  {openEvidence === incident.incidentId ? (
                    evidence === "loading" || evidence === undefined ? (
                      <p className="surface__state" role="status">
                        Loading evidence metadata...
                      </p>
                    ) : evidence === "error" ? (
                      <p className="surface__state surface__state--error" role="alert">
                        Could not load incident evidence.
                      </p>
                    ) : evidence.length === 0 ? (
                      <p className="surface__state" role="status">
                        No evidence attached.
                      </p>
                    ) : (
                      <ul className="incident__evidence">
                        {evidence.map((attachment) => (
                          <li key={attachment.attachmentId} className="incident__evidence-row">
                            <span>
                              {attachment.displayName ?? attachment.kind ?? "attachment"}
                            </span>
                            <span className="incident__evidence-meta">
                              {[
                                attachment.kind,
                                attachment.source,
                                attachment.storageAttached
                                  ? "bytes confirmed"
                                  : "unconfirmed",
                                attachment.sha256Present ? "integrity-hashed" : null,
                              ]
                                .filter(Boolean)
                                .join(" - ")}
                              {" - captured "}
                              <LiveTimestamp iso={attachment.capturedAt} timeZone={timeZone} />
                            </span>
                            {attachment.storageAttached ? (
                              <span className="incident__evidence-actions">
                                <button
                                  type="button"
                                  className="builder__navbtn"
                                  disabled={
                                    mediaRequests[attachment.attachmentId] === "opening"
                                  }
                                  onClick={() => {
                                    void openStoredMedia(attachment);
                                  }}
                                >
                                  {mediaRequests[attachment.attachmentId] === "opening"
                                    ? "Opening media..."
                                    : "Open/download media"}
                                </button>
                                {mediaRequests[attachment.attachmentId] === "error" ? (
                                  <span
                                    className="surface__state surface__state--error"
                                    role="alert"
                                  >
                                    Could not open evidence media.
                                  </span>
                                ) : null}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )
                  ) : null}
                </li>
              );
            })}
          </ul>
          <ResultFooter
            state={capped ? "capped" : windowCopy.state}
            foot={windowCopy.foot}
          />
        </div>
      ) : null}

      {canManage && lockConfirm !== null ? (
        <div
          className="minter__confirm"
          role="dialog"
          aria-label={`Lock incident - ${lockConfirm.title}`}
        >
          <h3 className="minter__confirm-title">Lock incident - {lockConfirm.title}</h3>
          <p className="minter__confirm-warning">
            Locking is final. It closes the incident to new evidence and uploads, and there is
            no unlock action here. The change is recorded against your account.
          </p>
          {reviewFailures[lockConfirm.incidentId] !== undefined ? (
            <p className="surface__state surface__state--error" role="alert">
              {reviewFailures[lockConfirm.incidentId]}
            </p>
          ) : null}
          <div className="minter__confirm-actions">
            <button
              type="button"
              className="builder__navbtn"
              disabled={reviewBusy[lockConfirm.incidentId] !== undefined}
              onClick={() => setLockConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="minter__confirm-go"
              disabled={reviewBusy[lockConfirm.incidentId] !== undefined}
              onClick={() => {
                void applyReviewAction(lockConfirm, "lock");
              }}
            >
              {reviewBusy[lockConfirm.incidentId] === "lock" ? "Locking..." : "Confirm lock"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
